import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Package, Key, Desktop, Printer, IdentificationCard, Books, MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockView = () => {
  const [keys, setKeys] = useState([]);
  const [itProducts, setItProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [k, p] = await Promise.all([
          axios.get(`${API_URL}/api/keys`, { withCredentials: true }),
          axios.get(`${API_URL}/api/it-products`, { withCredentials: true }),
        ]);
        setKeys(k.data);
        setItProducts(p.data);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const all = useMemo(() => {
    const merged = [
      ...keys.map(k => ({
        id: `key-${k.id}`,
        type: 'Clé',
        icon: Key,
        name: k.name,
        details: k.characteristics || '',
        price: k.sale_price,
        stock: k.stock_quantity,
        threshold: k.alert_threshold,
      })),
      ...itProducts.map(p => ({
        id: `it-${p.id}`,
        type: 'Produit IT',
        icon: Desktop,
        name: p.name,
        details: p.category + (p.description ? ` — ${p.description}` : ''),
        price: p.sale_price,
        stock: p.stock_quantity,
        threshold: p.alert_threshold,
        sku: p.sku,
      })),
    ];
    const q = search.toLowerCase();
    return q
      ? merged.filter(it =>
          it.name.toLowerCase().includes(q) ||
          (it.details || '').toLowerCase().includes(q) ||
          (it.sku || '').toLowerCase().includes(q)
        )
      : merged;
  }, [keys, itProducts, search]);

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="stock-view-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Stock marchandises</h1>
          <p className="text-slate-600">Consultation des produits disponibles et de leur prix de vente</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="stock-search-input"
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Catégorie</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Détails</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix vente TTC</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Stock</th>
                </tr>
              </thead>
              <tbody>
                {all.map(it => {
                  const Icon = it.icon;
                  const low = it.stock <= it.threshold;
                  return (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-2">
                          <Icon size={16} className="text-blue-600" /> {it.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{it.name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{it.details || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right font-semibold">
                        {Number(it.price || 0).toFixed(2)} €
                      </td>
                      <td className="py-3 px-4 text-sm text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                          it.stock === 0 ? 'bg-red-100 text-red-700' : low ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {it.stock} {it.stock === 0 ? '(rupture)' : low ? '(alerte)' : ''}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {all.length === 0 && (
                  <tr><td colSpan="5" className="py-12 text-center text-slate-500">Aucun produit</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockView;
